// src/components/HoursTable.jsx
// Tablica sati po tjednima - osobe, dani u tjednu (PON-NED), Glovo/Wolt/Ukupno sati

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import HoursRow from './HoursRow';
import { getWeeksInYear, getPreviousWeekIndex, getCurrentWeekIndex } from '../utils/weekUtils';
import { groupMetricsByUserId } from '../utils/metricsIndex';
import '../css/HoursTable.css';

function HoursTable({
  people = [],
  dailyMetricsData = [],
  holidaysByDate = {},
  selectedYear,
  selectedFirm,
  firms = [],
  refreshData,
  onExcelUpload,
  onUpdatePerson,
  onArchivePerson,
}) {
  const weeks = useMemo(() => getWeeksInYear(selectedYear), [selectedYear]);
  const scrollWrapperRef = useRef(null);
  const topScrollRef = useRef(null);
  const topScrollSpacerRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const defaultHiddenColumns = {
    actionsColumn: false,
    datumColumn: false,
    glovoSati: false,
    woltSati: false,
    ukupnoSati: false,
  };

  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('hoursTableColumns');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultHiddenColumns, ...parsed };
      }
    } catch (e) {
      console.warn('Greška pri učitavanju postavki stupaca:', e);
    }
    return defaultHiddenColumns;
  });

  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem('hoursTableColumns', JSON.stringify(hiddenColumns));
    } catch (e) {
      console.warn('Greška pri spremanju postavki stupaca:', e);
    }
  }, [hiddenColumns]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isColumnMenuOpen) setIsColumnMenuOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isColumnMenuOpen]);

  const toggleColumn = (key) => {
    setHiddenColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const allColumns = [
    { key: 'actionsColumn', label: 'Alati' },
    { key: 'datumColumn', label: 'Dan / Datum' },
    { key: 'glovoSati', label: 'Glovo sati' },
    { key: 'woltSati', label: 'Wolt sati' },
    { key: 'ukupnoSati', label: 'Ukupno sati' },
  ];

  const getFirstNameWord = (fullName) => {
    const normalized = String(fullName || '').trim();
    if (!normalized) return '';
    return normalized.split(/\s+/)[0] || '';
  };

  const filteredPeople = useMemo(() => {
    let filtered = [...people];

    if (selectedFirm) {
      filtered = filtered.filter((p) => p.firmId === selectedFirm);
    }

    filtered = filtered.filter((p) => {
      if (p.archived) {
        const archiveDate = new Date(p.archiveYear, p.archiveMonth - 1);
        const selectedDate = new Date(selectedYear, 11, 31);
        return archiveDate > selectedDate;
      }
      return true;
    });

    filtered = filtered.filter((p) => {
      const startYear = p.startYear ?? selectedYear;
      const startMonth = p.startMonth ?? 1;
      return startYear < selectedYear || (startYear === selectedYear && startMonth <= 12);
    });

    return filtered.sort((a, b) => {
      const firmA = firms.find((f) => f.id === a.firmId)?.name || 'Default';
      const firmB = firms.find((f) => f.id === b.firmId)?.name || 'Default';
      if (firmA !== firmB) return firmA.localeCompare(firmB);
      const nameCmp = getFirstNameWord(a.name).localeCompare(getFirstNameWord(b.name));
      if (nameCmp !== 0) return nameCmp;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [people, selectedFirm, selectedYear, firms]);

  const metricsByUserId = useMemo(
    () => groupMetricsByUserId(dailyMetricsData),
    [dailyMetricsData]
  );

  /* Širina stupca IME = najduža linija (ime ili prezime) u px, ~8px/znak za font 13px */
  const nameColWidth = useMemo(() => {
    let maxLen = 0;
    for (const p of filteredPeople) {
      const name = (p.name || '').trim();
      if (!name) continue;
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length <= 1) maxLen = Math.max(maxLen, name.length);
      else {
        const prezime = parts.pop();
        const ime = parts.join(' ');
        maxLen = Math.max(maxLen, ime.length, prezime.length);
      }
    }
    const px = Math.max(90, Math.min(220, maxLen * 8.5 + 16));
    return px;
  }, [filteredPeople]);

  useEffect(() => {
    if (!scrollWrapperRef.current || weeks.length === 0) return;
    const prevWeekIdx = getPreviousWeekIndex();
    const colsPerWeek =
      (!hiddenColumns.datumColumn ? 1 : 0) +
      (!hiddenColumns.glovoSati ? 1 : 0) +
      (!hiddenColumns.woltSati ? 1 : 0) +
      (!hiddenColumns.ukupnoSati ? 1 : 0) || 1;
    const scrollLeft = Math.max(0, prevWeekIdx * colsPerWeek * 52 - 80);
    scrollWrapperRef.current.scrollLeft = scrollLeft;
    if (topScrollRef.current) topScrollRef.current.scrollLeft = scrollLeft;
  }, [weeks.length, selectedYear]);

  const visibleColsPerWeek =
    (!hiddenColumns.datumColumn ? 1 : 0) +
    (!hiddenColumns.glovoSati ? 1 : 0) +
    (!hiddenColumns.woltSati ? 1 : 0) +
    (!hiddenColumns.ukupnoSati ? 1 : 0) || 1;

  const scrollToWeek = (weekIndex) => {
    if (!scrollWrapperRef.current) return;
    const colsPerWeek = visibleColsPerWeek;
    const colWidth = 52;
    const scrollLeft = Math.max(0, weekIndex * colsPerWeek * colWidth - 80);
    scrollWrapperRef.current.scrollLeft = scrollLeft;
    if (topScrollRef.current) topScrollRef.current.scrollLeft = scrollLeft;
  };

  const syncTopScrollWidth = useCallback(() => {
    if (scrollWrapperRef.current && topScrollSpacerRef.current) {
      topScrollSpacerRef.current.style.width = `${scrollWrapperRef.current.scrollWidth}px`;
    }
  }, []);

  useEffect(() => {
    syncTopScrollWidth();
  }, [syncTopScrollWidth, filteredPeople.length, weeks.length, hiddenColumns]);

  const rafIdRef = useRef(null);
  const handleTopScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (topScrollRef.current && scrollWrapperRef.current) {
        const newLeft = topScrollRef.current.scrollLeft;
        isProgrammaticScrollRef.current = true;
        scrollWrapperRef.current.scrollLeft = newLeft;
        requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
      }
    });
  }, []);

  const handleBottomScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      return;
    }
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (topScrollRef.current && scrollWrapperRef.current) {
        const newLeft = scrollWrapperRef.current.scrollLeft;
        isProgrammaticScrollRef.current = true;
        topScrollRef.current.scrollLeft = newLeft;
        requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
      }
    });
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const bottom = scrollWrapperRef.current;
    if (!top || !bottom) return;
    top.addEventListener('scroll', handleTopScroll, { passive: true });
    bottom.addEventListener('scroll', handleBottomScroll, { passive: true });
    return () => {
      top.removeEventListener('scroll', handleTopScroll);
      bottom.removeEventListener('scroll', handleBottomScroll);
    };
  }, [handleTopScroll, handleBottomScroll]);

  return (
    <div className="hours-table-container">
      <div className="hours-table-header">
        <span className="hours-people-count">Prikazano osoba: {filteredPeople.length}</span>
        <div className="hours-header-buttons">
          <button
            className="hours-ovaj-tjedan-btn"
            onClick={() => scrollToWeek(getCurrentWeekIndex(selectedYear))}
          >
            Ovaj tjedan
          </button>
          <div className="column-toggle-wrapper">
          <button
            className="column-toggle-button"
            onClick={() => setIsColumnMenuOpen(!isColumnMenuOpen)}
          >
            <span>Prikaži stupce</span>
            <span className={`dropdown-arrow ${isColumnMenuOpen ? 'open' : ''}`}>▼</span>
          </button>
          {isColumnMenuOpen && (
            <>
              <div
                className="dropdown-overlay"
                onClick={() => setIsColumnMenuOpen(false)}
              />
              <div className="column-dropdown-menu">
                <div className="dropdown-header">
                  <h3>Odaberi stupce za prikaz</h3>
                  <button className="close-dropdown" onClick={() => setIsColumnMenuOpen(false)}>
                    ×
                  </button>
                </div>
                <div className="dropdown-content">
                  {allColumns.map((col) => (
                    <label key={col.key} className="dropdown-item">
                      <input
                        type="checkbox"
                        checked={!hiddenColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      <div
        className="hours-top-scroll"
        ref={topScrollRef}
        aria-hidden
      >
        <div ref={topScrollSpacerRef} className="hours-top-scroll-spacer" />
      </div>
      <div className="hours-scroll-wrapper" ref={scrollWrapperRef}>
        <table className="hours-table">
          <colgroup>
            <col style={{ width: nameColWidth, minWidth: nameColWidth }} />
          </colgroup>
          <thead>
            <tr>
              <th className="hours-sticky-name">IME</th>
              {!hiddenColumns.actionsColumn && <th className="hours-sticky-actions"></th>}
              <th>DANI</th>
              {weeks.map((week) => (
                <th key={week.weekIndex} colSpan={visibleColsPerWeek || 1} className="hours-week-header">
                  {week.label} ({week.dates[0]?.day}.{week.dates[0]?.month}.–{week.dates[6]?.day}.{week.dates[6]?.month}.)
                </th>
              ))}
            </tr>
            <tr className="hours-subheader">
              <th className="hours-sticky-name"></th>
              {!hiddenColumns.actionsColumn && <th className="hours-sticky-actions"></th>}
              <th></th>
              {weeks.map((week) => (
                <React.Fragment key={week.weekIndex}>
                  {!hiddenColumns.datumColumn && <th className="hours-datum-header">Dan/Datum</th>}
                  {!hiddenColumns.glovoSati && <th className="hours-glovo-header">Glovo</th>}
                  {!hiddenColumns.woltSati && <th className="hours-wolt-header">Wolt</th>}
                  {!hiddenColumns.ukupnoSati && <th className="hours-ukupno-header">Ukupno</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredPeople.map((person) => (
              <HoursRow
                key={person.id}
                person={person}
                dailyMetricsData={metricsByUserId.get(person.id) || []}
                holidaysByDate={holidaysByDate}
                weeks={weeks}
                refreshData={refreshData}
                hiddenColumns={hiddenColumns}
                onExcelUpload={onExcelUpload}
                selectedFirm={selectedFirm}
                firms={firms}
                onUpdatePerson={onUpdatePerson}
                onArchivePerson={onArchivePerson}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(HoursTable);
